import type { PersonPoolMembership } from '../services/candidatePoolsApi';
import type { ViewerContext } from '../types/profileViewerContext';

/** Tab ids rendered in CandidateProfileModal (subset used for visibility rules). */
export type ProfileModalTabId =
  | 'overview'
  | 'experience'
  | 'skills'
  | 'education'
  | 'pools_source'
  | 'admin';

const ALL_PROFILE_MODAL_TABS: ProfileModalTabId[] = [
  'overview',
  'experience',
  'skills',
  'education',
  'pools_source',
  'admin',
];

const SELF_SERVICE_HIDDEN_TABS = new Set<ProfileModalTabId>(['pools_source', 'admin']);

const WRITABLE_POOL_ROLES = new Set(['owner', 'editor']);

/** HR/admin pool import — not magic-link self-service (CvImportCard handles that). */
export function usesPoolCvImport(viewerContext: ViewerContext): boolean {
  return viewerContext.kind === 'admin' || viewerContext.kind === 'hr';
}

/** Profile-update JWT routes (magic link or permanent employee session). */
export function usesSelfServiceCvImport(viewerContext: ViewerContext): boolean {
  return viewerContext.kind === 'self_session' || viewerContext.kind === 'self_magic';
}

export function showCvUploadInProfileModal(_viewerContext: ViewerContext): boolean {
  return true;
}

export function showProfileUpdateActions(viewerContext: ViewerContext): boolean {
  return (
    viewerContext.kind === 'admin' ||
    viewerContext.kind === 'hr' ||
    viewerContext.kind === 'self_session' ||
    viewerContext.kind === 'self_magic'
  );
}

/** Profile-update magic link and permanent employee session — no HR/admin tabs. */
export function profileTabsForViewer(viewerContext: ViewerContext): ProfileModalTabId[] {
  if (viewerContext.kind === 'self_magic' || viewerContext.kind === 'self_session') {
    return ALL_PROFILE_MODAL_TABS.filter((tab) => !SELF_SERVICE_HIDDEN_TABS.has(tab));
  }
  return ALL_PROFILE_MODAL_TABS;
}

/** Prefer a writable pool that already lists this person (profile-modal import). */
export function pickWritablePoolForPerson(
  memberships: PersonPoolMembership[],
  personId: number
): number | null {
  const writable = memberships.filter((m) => WRITABLE_POOL_ROLES.has(m.my_role));
  const memberPool = writable.find((m) => m.person_id === personId);
  if (memberPool) return memberPool.pool_id;
  return writable[0]?.pool_id ?? null;
}

export function pickWritablePoolId(memberships: PersonPoolMembership[]): number | null {
  const writable = memberships.filter((m) => WRITABLE_POOL_ROLES.has(m.my_role));
  return writable[0]?.pool_id ?? null;
}
